import { FC } from 'react';
import styled from 'styled-components/macro';

import { Validator } from 'helpers/validators';
import { Input } from './Input';
import { PasswordStrengthChecker } from './PasswordStrengthChecker';
import { useTheme } from 'hooks/theme';
import { useFieldState } from 'informed';
import { useToggle } from '@repo-level/core-front';

interface PasswordProps {
  field: string;
  placeholder?: string;
  animatedPlaceholder?: string;
  validate?: Validator<any, any>;
  error?: React.ReactNode;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  strengthChecker?: boolean;
  autoComplete?: string;
}

export const Password: FC<PasswordProps> = ({ strengthChecker, ...props }) => {
  const [showPassword, toggleShowPassword] = useToggle(false);
  const password = useFieldState<string | undefined>(props.field);

  const { images } = useTheme();

  return (
    <ScPassword>
      <ScPasswordInputContainer>
        <Input
          type={showPassword ? 'text' : 'password'}
          hideErrorStyles={!!password?.value?.length && strengthChecker}
          error={props.error}
          addons={
            <ScButtonEye
              type="button"
              onClick={toggleShowPassword}
              isOpen={showPassword}
            >
              <div className="eye opened-eye">
                <images.eyeOpenIcon.component />
              </div>
              <div className="eye closed-eye">
                <images.eyeClosedIcon.component />
              </div>
            </ScButtonEye>
          }
          {...props}
        />
        {(password?.value?.length ?? 0) > 0 && strengthChecker && (
          <PasswordStrengthChecker field={props.field} />
        )}
      </ScPasswordInputContainer>
    </ScPassword>
  );
};

interface ScButtonEyeProps {
  isOpen: boolean;
}

const ScPassword = styled.div`
  position: relative;
`;

const ScPasswordInputContainer = styled.div`
  position: relative;
`;

export const ScButtonEye = styled.button<ScButtonEyeProps>`
  width: 24px;
  height: 100%;
  background: none;
  border: none;
  cursor: pointer;

  .eye {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    svg {
      margin-right: 0;

      path,
      circle,
      g,
      .clip,
      .eye {
        transition: 0.3s;
      }
    }
  }

  .opened-eye {
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  }

  .closed-eye {
    opacity: ${({ isOpen }) => (!isOpen ? 1 : 0)};
  }

  &:hover {
    path,
    circle,
    g,
    .clip {
      fill: #fff;
      animation-fill-mode: forwards;
    }
  }
`;
